import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';
import {
  type AspectRatioOption,
  type BorderOptions,
  canvasToBlob,
  getAspectRatioCropPreview,
  getCanvasLogicalContentSize,
  getCropRegion,
  getOutputSize,
  normalizedCropHeightForWidthHeightRatio,
  normalizedCropWidthForWidthHeightRatio,
  type IconItem,
  type OutputFormatOption,
  type PipelineStepId,
  loadBlobToCanvas,
  runImagePipeline,
  type WatermarkItem,
} from '../lib/image-editor/imagePipeline';
import {
  clampIconQrSizeMin,
  clampWatermarkFontSizeMin,
} from '../lib/image-editor/overlayLimits';

/** 單一圖示塊（含 id 供 UI 增刪用） */
export interface IconBlock extends IconItem {
  readonly id: string;
}

/** 單一文字塊（含 id 供 UI 增刪用） */
export interface WatermarkBlock extends WatermarkItem {
  readonly id: string;
}

/** 單一 QR Code 塊（含 id 供 UI 增刪用） */
export interface QRCodeBlock {
  readonly id: string;
  content: string;
  size: number;
  anchorX: number;
  anchorY: number;
  /** 碼色（前景），CSS 顏色，預設 #000000 */
  darkColor?: string;
  /** 背景色，CSS 顏色，預設 #ffffff */
  lightColor?: string;
}

const ASPECT_RATIOS: AspectRatioOption[] = [
  { id: 'original', label: '自由', ratio: null },
  { id: '1:1', label: '1:1', ratio: 1 },
  { id: '4:3', label: '4:3', ratio: 4 / 3 },
  { id: '3:4', label: '3:4', ratio: 3 / 4 },
  { id: '16:9', label: '16:9', ratio: 16 / 9 },
  { id: '9:16', label: '9:16', ratio: 9 / 16 },
];

/** 多圖合併時可選的輸出畫布寬高比（自由 = 依內容，其餘為固定比例並留白） */
const MERGE_ASPECT_RATIOS: AspectRatioOption[] = [
  { id: 'free', label: '自由', ratio: null },
  { id: '1:1', label: '1:1', ratio: 1 },
  { id: '4:3', label: '4:3', ratio: 4 / 3 },
  { id: '3:4', label: '3:4', ratio: 3 / 4 },
  { id: '16:9', label: '16:9', ratio: 16 / 9 },
  { id: '9:16', label: '9:16', ratio: 9 / 16 },
];

/** 多圖合併排版：自動網格（如 4 張→2×2）、垂直、橫向 */
export interface MergeLayoutOption {
  readonly id: string;
  readonly label: string;
}

const MERGE_LAYOUTS: MergeLayoutOption[] = [
  { id: 'auto', label: '自動（網格）' },
  { id: 'vertical', label: '垂直堆疊' },
  { id: 'horizontal', label: '橫向排列' },
];

const OUTPUT_FORMATS: OutputFormatOption[] = [
  { id: 'png', label: 'PNG', mime: 'image/png', ext: 'png' },
  { id: 'jpeg', label: 'JPEG', mime: 'image/jpeg', ext: 'jpg', quality: 0.92 },
  { id: 'webp', label: 'WebP', mime: 'image/webp', ext: 'webp', quality: 0.9 },
];

const ACCEPT_IMAGE = 'image/*';
const MAX_PREVIEW = 400;
const MIN_PX = 1;
const MAX_PX = 8000;

const PIPELINE_STEP_LABELS: Record<PipelineStepId, string> = {
  removeBackground: '去背',
  aspectRatio: '長寬比裁切',
  upscale: 'AI 放大',
  resize: '輸出尺寸',
  filters: '濾鏡',
  border: '邊線處理',
  icons: '圖示',
  watermark: '文字／浮水印',
  qrcode: 'QR Code',
  format: '輸出格式',
};

export interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ImageSize {
  readonly width: number;
  readonly height: number;
}

export interface UseImageEditorResult {
  readonly acceptImage: string;
  readonly maxPreview: number;
  readonly minPx: number;
  readonly maxPx: number;
  readonly aspectRatios: readonly AspectRatioOption[];
  readonly outputFormats: readonly OutputFormatOption[];
  readonly file: File | null;
  /** 目前選取的檔案列表（多選時為多筆，單張時為一筆） */
  readonly files: File[];
  /** 目前編輯中的檔案索引（0-based） */
  readonly currentFileIndex: number;
  setCurrentFileIndex(index: number): void;
  /** 從列表中移除第 index 張圖片（0-based） */
  removeFileAt(index: number): void;
  readonly objectUrl: string | null;
  readonly originalSize: ImageSize | null;
  readonly previewUrl: string | null;
  /** 裁切區塊使用的原始預覽圖（裁切預覽只套用在上方畫布，不影響此圖） */
  readonly cropSourceUrl: string | null;
  readonly noBackgroundUrl: string | null;
  readonly upscaledUrl: string | null;
  readonly isDragging: boolean;
  readonly aspectRatioId: string;
  readonly formatId: string;
  readonly outputWidth: string;
  readonly outputHeight: string;
  /** 失焦後才套用的輸出畫布寬高，用於 pipeline */
  readonly committedOutputWidth: number;
  readonly committedOutputHeight: number;
  /** 即時預覽用畫布寬高（輸入框值或 committed），用於畫布預覽與 canvasContentSize */
  readonly effectiveOutputWidth: number;
  readonly effectiveOutputHeight: number;
  readonly isCroppingMode: boolean;
  readonly cropRect: CropRect | null;
  readonly cropApplying: boolean;
  readonly processing: boolean;
  readonly error: string | null;
  readonly removeBackgroundLoading: boolean;
  readonly upscaleLoading: boolean;
  readonly upscaleFactor: 2 | 4;
  readonly useSmartCrop: boolean;
  readonly brightness: number;
  readonly contrast: number;
  readonly saturation: number;
  /** 邊線處理：形狀（rect／rounded／circle）、圓角、邊線粗細與顏色 */
  readonly borderShape: BorderOptions['shape'];
  readonly borderCornerRadius: number;
  readonly borderWidth: number;
  readonly borderColor: string;
  /** 圖示層：多個圖示（Lucide／品牌 Logo），在文字浮水印之前 */
  readonly iconItems: readonly IconBlock[];
  readonly iconPadding: number;
  /** 文字／浮水印：多個文字塊（畫布尺寸之後） */
  readonly watermarkItems: readonly WatermarkBlock[];
  /** QR Code：多個項目，每項含內容、尺寸、錨點 */
  readonly qrCodeItems: readonly QRCodeBlock[];
  /** 預覽用 data URL，key 為 item.id */
  readonly qrCodePreviewUrls: Record<string, string>;
  addQrCodeItem(): void;
  removeQrCodeItem(id: string): void;
  updateQrCodeItem(id: string, patch: Partial<Omit<QRCodeBlock, 'id'>>): void;
  handleResetQrCode(): void;
  readonly outputAllowPadding: boolean;
  /** 畫布背景：純色 或 圖片 */
  readonly outputBackgroundType: 'color' | 'image';
  readonly outputBackgroundColor: string;
  /** 畫布背景圖（data URL），僅當 outputBackgroundType === 'image' 時使用 */
  readonly outputBackgroundImage: string | null;
  readonly canvasAnchorX: number;
  readonly canvasAnchorY: number;
  /** 壓縮比例 0.1–1（十格），用於 JPEG/WebP 輸出品質；1 為不壓縮 */
  readonly compressionRatio: number;
  /** 輸出尺寸比例 0.1–1（十格），套用後輸出寬高會按比例縮小，預估尺寸即時反映 */
  readonly outputScaleRatio: number;
  /** 手動指定輸出寬高（px），有值時優先於比例，可輸入任意尺寸；null 表示使用比例 */
  readonly targetOutputPixels: ImageSize | null;
  /** 依目前設定即時計算的預估輸出尺寸（px），已含尺寸比例或手動尺寸；輸出一律以畫布為準，無圖時為 null */
  readonly estimatedOutputSize: ImageSize | null;
  /** 輸出畫布上「圖片內容」的邏輯尺寸（px），用於預覽比例；未設輸出尺寸時為 null */
  readonly canvasContentSize: ImageSize | null;
  /** 目前圖片內容尺寸（原圖或裁切＋放大後），畫布不得小於此；與畫布不同：畫布可自訂更大並留白；無圖時為 null */
  readonly minOutputCanvasSize: ImageSize | null;
  /** 畫布尺寸模式：原圖大小（預設）或自訂大小 */
  readonly canvasSizeMode: 'sameAsImage' | 'custom';
  readonly setCanvasSizeMode: (mode: 'sameAsImage' | 'custom') => void;
  readonly pipelineCurrentStep: string | null;
  readonly pipelineCompletedSteps: readonly string[];
  readonly imgRef: React.RefObject<HTMLImageElement>;
  /** 將目前輸出畫布切成網格（9/16/25 格），並下載為 ZIP；支援多張圖片批次處理 */
  handleNineGridSlice(): Promise<void>;
  /** 輸出方式：null＝一般輸出，9/16/25＝網格切割 ZIP，'merge'＝多圖合併 */
  readonly gridSliceCount: null | 9 | 16 | 25 | 'merge';
  setGridSliceCount(count: null | 9 | 16 | 25 | 'merge'): void;
  /** 將多張圖片合併成一張（垂直堆疊）並下載；需至少 2 張圖 */
  handleMergeImages(): Promise<void>;
  /** 多圖合併時的輸出畫布寬高比選項 */
  readonly mergeAspectRatios: readonly AspectRatioOption[];
  /** 目前選取的多圖合併寬高比 id */
  readonly mergeAspectRatioId: string;
  setMergeAspectRatioId(id: string): void;
  /** 多圖合併排版選項（自動網格／垂直／橫向） */
  readonly mergeLayouts: readonly MergeLayoutOption[];
  readonly mergeLayoutId: string;
  setMergeLayoutId(id: string): void;
  handleFileChange(event: React.ChangeEvent<HTMLInputElement>): void;
  handleDragOver(event: React.DragEvent): void;
  handleDragLeave(event: React.DragEvent): void;
  handleDrop(event: React.DragEvent): void;
  handleToggleCropMode(): void;
  handleResetCrop(): void;
  handleCropMouseDown(
    event: React.MouseEvent<HTMLDivElement>,
    mode: 'move' | 'nw' | 'ne' | 'sw' | 'se'
  ): void;
  handleCropWidthPxChange(event: React.ChangeEvent<HTMLInputElement>): void;
  handleCropHeightPxChange(event: React.ChangeEvent<HTMLInputElement>): void;
  /** 「自由」裁切時：切換是否鎖定目前裁切寬高比（角落拖拉維持比例；輸入一邊時自動換算另一邊） */
  toggleFreeCropAspectLock(): void;
  readonly cropFreeAspectRatioLocked: boolean;
  handleApplyCrop(): Promise<void>;
  handleRemoveBackground(): Promise<void>;
  handleClearNoBackground(): void;
  handleUpscale(scale: 2 | 4): Promise<void>;
  handleClearUpscale(): void;
  /** 可傳入 pipelineCropRect 使輸出與預覽裁切一致（由頁面依 cropSizeMode 決定是否傳入） */
  handleOutput(options?: { pipelineCropRect?: CropRect | null }): Promise<void>;
  setAspectRatioId(id: string): void;
  setFormatId(id: string): void;
  setOutputWidth(value: string): void;
  setOutputHeight(value: string): void;
  setUseSmartCrop(next: boolean): void;
  setUpscaleFactor(factor: 2 | 4): void;
  setBrightness(value: number): void;
  setContrast(value: number): void;
  setSaturation(value: number): void;
  setBorderShape(shape: BorderOptions['shape']): void;
  setBorderCornerRadius(value: number): void;
  setBorderWidth(value: number): void;
  setBorderColor(value: string): void;
  handleResetBorder(): void;
  setIconPadding(value: number): void;
  addIconItem(item: Omit<IconBlock, 'id'>): void;
  removeIconItem(id: string): void;
  updateIconItem(id: string, patch: Partial<Omit<IconBlock, 'id'>>): void;
  handleResetIcons(): void;
  addWatermarkItem(): void;
  removeWatermarkItem(id: string): void;
  updateWatermarkItem(
    id: string,
    patch: Partial<Omit<WatermarkBlock, 'id'>>
  ): void;
  handleResetWatermark(): void;
  setOriginalSize(size: ImageSize): void;
  setError(message: string | null): void;
  setOutputAllowPadding(next: boolean): void;
  setOutputBackgroundType(type: 'color' | 'image'): void;
  setOutputBackgroundColor(value: string): void;
  setOutputBackgroundImage(dataUrl: string | null): void;
  setCanvasAnchor(anchor: { x: number; y: number }): void;
  setCompressionRatio(value: number): void;
  setOutputScaleRatio(value: number): void;
  /** 設定手動輸出尺寸（px），會覆蓋比例；傳入皆 ≥ MIN_PX 才生效 */
  setTargetOutputPixels(width: number, height: number): void;
  /** 清除手動輸出尺寸，改回使用比例 */
  clearTargetOutputPixels(): void;
  /** 將目前輸入的寬高 commit，畫布與預覽於 onBlur 時呼叫 */
  commitOutputSize(): void;
  /** 依長寬比預設設定畫布寬高（至少為當前圖尺寸），無圖時不動作 */
  applyCanvasAspectRatio(ratioId: string): void;
  /** 裁切來源圖（cropSourceUrl）載入完成時由 CropperPanel 呼叫，用來在去背／放大後重算裁切預覽 */
  notifyCropSourceLoaded(): void;
}

export function useImageEditor(): UseImageEditorResult {
  const [file, setFile] = useState<File | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [currentFileIndex, setCurrentFileIndexState] = useState(0);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [aspectRatioId, setAspectRatioId] = useState('original');
  const [formatId, setFormatId] = useState('png');
  const [outputWidth, setOutputWidth] = useState('');
  const [outputHeight, setOutputHeight] = useState('');
  const [committedOutputWidth, setCommittedOutputWidth] = useState(0);
  const [committedOutputHeight, setCommittedOutputHeight] = useState(0);
  const [originalSize, setOriginalSize] = useState<ImageSize | null>(null);
  const [noBackgroundUrl, setNoBackgroundUrl] = useState<string | null>(null);
  const [removeBackgroundLoading, setRemoveBackgroundLoading] = useState(false);
  const [useSmartCrop, setUseSmartCrop] = useState(false);
  const [upscaledUrl, setUpscaledUrl] = useState<string | null>(null);
  const [cropPreviewUrl, setCropPreviewUrl] = useState<string | null>(null);
  const [cropPreviewSize, setCropPreviewSize] = useState<ImageSize | null>(
    null
  );
  /** 裁切來源圖載入時遞增，用來觸發裁切預覽 effect 重算（裁切後去背／放大時保留裁切預覽） */
  const [cropSourceLoadedKey, setCropSourceLoadedKey] = useState(0);
  const [upscaleLoading, setUpscaleLoading] = useState(false);
  /** 預覽圖目前套用的放大倍率（1=無、2/4=點過 2×/4×），用於畫布預覽的內容尺寸 */
  const [previewUpscaleFactor, setPreviewUpscaleFactor] = useState<1 | 2 | 4>(
    1
  );
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [isCroppingMode, setIsCroppingMode] = useState(false);
  const [cropRect, setCropRect] = useState<CropRect | null>(null);
  const [cropApplying, setCropApplying] = useState(false);
  /** 自由裁切時鎖定寬高比（像素寬／像素高），供輸入與角落拖拉共用 */
  const [cropFreeAspectRatioLocked, setCropFreeAspectRatioLocked] =
    useState(false);
  const freeCropLockRatioRef = useRef<number | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  /** 是否正在拖曳裁切框（拖拉過程暫不更新預覽，放開後再更新一次） */
  const [isCropRectDragging, setIsCropRectDragging] = useState(false);

  const currentRatio = ASPECT_RATIOS.find(ratio => ratio.id === aspectRatioId);
  const currentFormat = OUTPUT_FORMATS.find(format => format.id === formatId)!;

  const cropDragStateRef = useRef<{
    mode: 'move' | 'nw' | 'ne' | 'sw' | 'se' | null;
    startX: number;
    startY: number;
    startRect: CropRect | null;
    /** 自由＋鎖定時：裁切框寬／高（像素比），滑鼠移動時優先於預設長寬比 */
    freeLockRatio: number | null;
  } | null>(null);

  /** 拖曳裁切框時需讀取最新長寬比，避免閉包過期導致未鎖定比例 */
  const currentRatioRef = useRef(currentRatio);
  currentRatioRef.current = currentRatio;

  const [upscaleFactor, setUpscaleFactor] = useState<2 | 4>(2);

  const [brightness, setBrightness] = useState(0);
  const [contrast, setContrast] = useState(0);
  const [saturation, setSaturation] = useState(0);

  const [borderShape, setBorderShape] =
    useState<BorderOptions['shape']>('rect');
  const [borderCornerRadius, setBorderCornerRadius] = useState(0);
  const [borderWidth, setBorderWidth] = useState(0);
  const [borderColor, setBorderColor] = useState('#000000');

  const nextIconIdRef = useRef(0);
  const nextWatermarkIdRef = useRef(0);
  const [iconItems, setIconItems] = useState<IconBlock[]>([]);
  const [iconPadding, setIconPadding] = useState(0);

  const [watermarkItems, setWatermarkItems] = useState<WatermarkBlock[]>([
    {
      id: 'w-0',
      text: '',
      fontFamily: 'sans-serif',
      fontSize: 24,
      color: '#000000',
      opacity: 0.7,
      anchorX: 0.5,
      anchorY: 0.5,
    },
  ]);

  const [qrCodeItems, setQrCodeItems] = useState<QRCodeBlock[]>([]);
  const [qrCodePreviewUrls, setQrCodePreviewUrls] = useState<
    Record<string, string>
  >({});

  const [outputAllowPadding, setOutputAllowPadding] = useState(false);
  const [outputBackgroundType, setOutputBackgroundType] = useState<
    'color' | 'image'
  >('color');
  const [outputBackgroundColor, setOutputBackgroundColor] = useState('#ffffff');
  const [outputBackgroundImage, setOutputBackgroundImage] = useState<
    string | null
  >(null);
  const [canvasAnchorX, setCanvasAnchorX] = useState(0.5);
  const [canvasAnchorY, setCanvasAnchorY] = useState(0.5);
  const [canvasSizeMode, setCanvasSizeMode] = useState<
    'sameAsImage' | 'custom'
  >('sameAsImage');

  /** 壓縮比例 0.1–1（十格），僅影響 JPEG/WebP 輸出品質 */
  const [compressionRatio, setCompressionRatioState] = useState(1);

  /** 輸出尺寸比例 0.1–1（十格），輸出寬高按此比例縮小 */
  const [outputScaleRatio, setOutputScaleRatioState] = useState(1);

  /** 手動指定輸出寬高（px），有值時 pipeline 使用此尺寸、忽略比例 */
  const [targetOutputPixels, setTargetOutputPixelsState] =
    useState<ImageSize | null>(null);

  const [pipelineCurrentStep, setPipelineCurrentStep] = useState<string | null>(
    null
  );
  const [pipelineCompletedSteps, setPipelineCompletedSteps] = useState<
    string[]
  >([]);

  /** 多圖合併時的輸出畫布寬高比（自由 = 依內容） */
  const [mergeAspectRatioId, setMergeAspectRatioId] = useState('free');

  /** 多圖合併排版（自動網格／垂直／橫向） */
  const [mergeLayoutId, setMergeLayoutId] = useState('auto');

  /** 輸出方式：null＝一般輸出，9/16/25＝網格切割 ZIP，'merge'＝多圖合併 */
  const [gridSliceCount, setGridSliceCount] = useState<
    null | 9 | 16 | 25 | 'merge'
  >(null);

  const setCompressionRatio = useCallback((value: number) => {
    const clamped = Math.min(1, Math.max(0.1, value));
    const step = 0.1;
    setCompressionRatioState(Math.round(clamped / step) * step);
  }, []);

  const setOutputScaleRatio = useCallback((value: number) => {
    const clamped = Math.min(1, Math.max(0.1, value));
    const step = 0.1;
    setOutputScaleRatioState(Math.round(clamped / step) * step);
  }, []);

  const setTargetOutputPixels = useCallback((width: number, height: number) => {
    const w = Math.min(MAX_PX, Math.max(MIN_PX, Math.floor(width)));
    const h = Math.min(MAX_PX, Math.max(MIN_PX, Math.floor(height)));
    if (w >= MIN_PX && h >= MIN_PX) {
      setTargetOutputPixelsState({ width: w, height: h });
    }
  }, []);

  const clearTargetOutputPixels = useCallback(() => {
    setTargetOutputPixelsState(null);
  }, []);

  const minOutputCanvasSize: ImageSize | null = (() => {
    const base = cropPreviewSize ?? originalSize;
    if (!base?.width || !base?.height) return null;
    let baseW = base.width;
    let baseH = base.height;
    if (
      aspectRatioId !== 'original' &&
      currentRatio?.ratio != null &&
      currentRatio.ratio > 0
    ) {
      const [, , sw, sh] = getCropRegion(
        base.width,
        base.height,
        currentRatio.ratio
      );
      baseW = Math.round(sw);
      baseH = Math.round(sh);
    }
    /** 已執行 AI 放大時，預覽圖為放大後尺寸，「目前圖片」需反映實際顯示尺寸 */
    if (upscaledUrl && previewUpscaleFactor > 1) {
      baseW *= previewUpscaleFactor;
      baseH *= previewUpscaleFactor;
    }
    return { width: baseW, height: baseH };
  })();

  const estimatedOutputSize: ImageSize | null = (() => {
    if (targetOutputPixels?.width && targetOutputPixels?.height) {
      return targetOutputPixels;
    }
    const scale = Math.min(1, Math.max(0.1, outputScaleRatio));
    const minW = minOutputCanvasSize?.width ?? 0;
    const minH = minOutputCanvasSize?.height ?? 0;
    const rawW =
      Math.floor(Number(String(outputWidth).replace(/\D/g, ''))) || 0;
    const rawH =
      Math.floor(Number(String(outputHeight).replace(/\D/g, ''))) || 0;
    const effectiveW = rawW > 0 ? Math.max(rawW, minW) : committedOutputWidth;
    const effectiveH = rawH > 0 ? Math.max(rawH, minH) : committedOutputHeight;
    /** 輸出一律以畫布為準：畫布 = 原圖大小或自訂寬高，預估 = 畫布 × 比例 */
    if (effectiveW > 0 && effectiveH > 0) {
      return {
        width: Math.max(MIN_PX, Math.round(effectiveW * scale)),
        height: Math.max(MIN_PX, Math.round(effectiveH * scale)),
      };
    }
    const orig = originalSize;
    if (!orig?.width || !orig?.height) return null;

    let baseW = orig.width;
    let baseH = orig.height;

    if (
      aspectRatioId !== 'original' &&
      currentRatio?.ratio != null &&
      currentRatio.ratio > 0
    ) {
      const [, , sw, sh] = getCropRegion(
        orig.width,
        orig.height,
        currentRatio.ratio
      );
      baseW = Math.round(sw);
      baseH = Math.round(sh);
    }

    if (upscaledUrl && previewUpscaleFactor > 1) {
      baseW *= previewUpscaleFactor;
      baseH *= previewUpscaleFactor;
    }

    const [resizedW, resizedH] = getOutputSize(
      baseW,
      baseH,
      effectiveW,
      effectiveH
    );
    return {
      width: Math.max(MIN_PX, Math.round(resizedW * scale)),
      height: Math.max(MIN_PX, Math.round(resizedH * scale)),
    };
  })();

  /** 即時預覽用畫布寬高：輸入框有效值（且 ≥ min）或 committed，讓預覽在輸入當下就反映畫布與左右留白 */
  const effectiveOutputWidth = (() => {
    const rawW =
      Math.floor(Number(String(outputWidth).replace(/\D/g, ''))) || 0;
    const minW = minOutputCanvasSize?.width ?? 0;
    return rawW > 0 ? Math.max(rawW, minW) : committedOutputWidth;
  })();
  const effectiveOutputHeight = (() => {
    const rawH =
      Math.floor(Number(String(outputHeight).replace(/\D/g, ''))) || 0;
    const minH = minOutputCanvasSize?.height ?? 0;
    return rawH > 0 ? Math.max(rawH, minH) : committedOutputHeight;
  })();

  /** 畫布內圖層邏輯像素：畫布較大時維持原圖 1:1（不放大填滿）；僅在圖超出畫布時等比例縮小 */
  const canvasContentSize: ImageSize | null = (() => {
    const min = minOutputCanvasSize;
    if (!min?.width || !min?.height) return null;

    const wantW = effectiveOutputWidth;
    const wantH = effectiveOutputHeight;
    if (wantW <= 0 && wantH <= 0) return null;

    if (wantW > 0 && wantH > 0) {
      return getCanvasLogicalContentSize(min.width, min.height, wantW, wantH);
    }
    const [cw, ch] = getOutputSize(min.width, min.height, wantW, wantH);
    return { width: cw, height: ch };
  })();

  const commitOutputSize = useCallback(() => {
    const rawW =
      Math.floor(Number(String(outputWidth).replace(/\D/g, ''))) || 0;
    const rawH =
      Math.floor(Number(String(outputHeight).replace(/\D/g, ''))) || 0;
    const minW = minOutputCanvasSize?.width ?? 0;
    const minH = minOutputCanvasSize?.height ?? 0;
    setCommittedOutputWidth(rawW > 0 ? Math.max(rawW, minW) : 0);
    setCommittedOutputHeight(rawH > 0 ? Math.max(rawH, minH) : 0);
  }, [
    outputWidth,
    outputHeight,
    minOutputCanvasSize?.width,
    minOutputCanvasSize?.height,
  ]);

  /** 依長寬比預設設定畫布寬高（至少為當前圖尺寸），無圖時不動作 */
  const applyCanvasAspectRatio = useCallback(
    (ratioId: string) => {
      const min = minOutputCanvasSize;
      if (!min?.width || !min.height) return;
      const minW = min.width;
      const minH = min.height;
      const option = ASPECT_RATIOS.find(r => r.id === ratioId);
      let w: number;
      let h: number;
      if (!option || option.ratio == null || option.ratio <= 0) {
        w = minW;
        h = minH;
      } else {
        const r = option.ratio;
        h = Math.ceil(Math.max(minH, minW / r));
        w = Math.round(r * h);
        if (w < minW) {
          w = minW;
          h = Math.ceil(minW / r);
        }
      }
      w = Math.min(MAX_PX, Math.max(MIN_PX, w));
      h = Math.min(MAX_PX, Math.max(MIN_PX, h));
      setOutputWidth(String(w));
      setOutputHeight(String(h));
      setCommittedOutputWidth(w);
      setCommittedOutputHeight(h);
    },
    [minOutputCanvasSize?.width, minOutputCanvasSize?.height]
  );

  const basePreviewUrl = upscaledUrl ?? noBackgroundUrl ?? objectUrl;
  const previewUrl = cropPreviewUrl ?? basePreviewUrl;
  const cropSourceUrl = basePreviewUrl;

  /** 基底圖（去背／放大）變更時：清除舊裁切預覽；若已有裁切設定則用新圖主動重算，無需等使用者點裁切分頁 */
  useEffect(() => {
    setCropPreviewUrl(previous => {
      if (previous) URL.revokeObjectURL(previous);
      return null;
    });
    setCropPreviewSize(null);

    const url = upscaledUrl ?? noBackgroundUrl ?? objectUrl;
    const hasCrop =
      cropRect !== null || (useSmartCrop && aspectRatioId !== 'original');
    if (!url || !hasCrop) return;

    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;

      if (useSmartCrop && aspectRatioId !== 'original') {
        const ratioOption = ASPECT_RATIOS.find(r => r.id === aspectRatioId);
        const ratio = ratioOption?.ratio ?? 0;
        if (ratio <= 0) return;
        void getAspectRatioCropPreview(img, ratio, true).then(canvas => {
          if (cancelled || !canvas) return;
          setCropPreviewSize({ width: canvas.width, height: canvas.height });
          canvas.toBlob(result => {
            if (cancelled || !result) return;
            const nextUrl = URL.createObjectURL(result);
            setCropPreviewUrl(prev => {
              if (prev) URL.revokeObjectURL(prev);
              return nextUrl;
            });
          }, 'image/png');
        });
        return;
      }

      if (!cropRect) return;
      const naturalWidth = img.naturalWidth;
      const naturalHeight = img.naturalHeight;
      if (!naturalWidth || !naturalHeight) return;
      const startX = Math.round(cropRect.x * naturalWidth);
      const startY = Math.round(cropRect.y * naturalHeight);
      const width = Math.round(cropRect.width * naturalWidth);
      const height = Math.round(cropRect.height * naturalHeight);
      if (width < MIN_PX || height < MIN_PX) return;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      if (!context) return;
      context.drawImage(
        img,
        startX,
        startY,
        width,
        height,
        0,
        0,
        width,
        height
      );
      setCropPreviewSize({ width, height });
      canvas.toBlob(result => {
        if (cancelled || !result) return;
        const nextUrl = URL.createObjectURL(result);
        setCropPreviewUrl(prev => {
          if (prev) URL.revokeObjectURL(prev);
          return nextUrl;
        });
      }, 'image/png');
    };
    img.src = url;

    return () => {
      cancelled = true;
    };
  }, [noBackgroundUrl, upscaledUrl, objectUrl]);

  /** 裁切或智能裁切時，在上方「預覽」區塊顯示結果；拖曳過程暫不更新，放開後再更新一次 */
  useEffect(() => {
    if (!imgRef.current) {
      setCropPreviewUrl(previous => {
        if (previous) URL.revokeObjectURL(previous);
        return null;
      });
      setCropPreviewSize(null);
      return;
    }

    const imageElement = imgRef.current;

    // 智能裁切預覽（依主體）
    if (useSmartCrop && aspectRatioId !== 'original') {
      const ratioOption = ASPECT_RATIOS.find(r => r.id === aspectRatioId);
      const ratio = ratioOption?.ratio ?? 0;
      if (ratio <= 0) {
        setCropPreviewUrl(previous => {
          if (previous) URL.revokeObjectURL(previous);
          return null;
        });
        setCropPreviewSize(null);
        return;
      }

      let cancelled = false;
      (async () => {
        const canvas = await getAspectRatioCropPreview(
          imageElement,
          ratio,
          true
        );
        if (!canvas || cancelled) return;

        setCropPreviewSize({ width: canvas.width, height: canvas.height });

        canvas.toBlob(result => {
          if (!result || cancelled) return;
          const nextUrl = URL.createObjectURL(result);
          setCropPreviewUrl(previous => {
            if (previous) URL.revokeObjectURL(previous);
            return nextUrl;
          });
        }, 'image/png');
      })();

      return () => {
        cancelled = true;
      };
    }

    // 一般裁切預覽：依目前裁切框顯示
    if (!cropRect) {
      setCropPreviewUrl(previous => {
        if (previous) URL.revokeObjectURL(previous);
        return null;
      });
      setCropPreviewSize(null);
      return;
    }

    // 正在拖曳裁切框時，不即時重算預覽，避免拖拉卡頓
    if (isCropRectDragging) {
      return;
    }

    const naturalWidth = imageElement.naturalWidth;
    const naturalHeight = imageElement.naturalHeight;
    if (!naturalWidth || !naturalHeight) return;

    const startX = Math.round(cropRect.x * naturalWidth);
    const startY = Math.round(cropRect.y * naturalHeight);
    const width = Math.round(cropRect.width * naturalWidth);
    const height = Math.round(cropRect.height * naturalHeight);
    if (width < MIN_PX || height < MIN_PX) return;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) return;

    context.drawImage(
      imageElement,
      startX,
      startY,
      width,
      height,
      0,
      0,
      width,
      height
    );

    setCropPreviewSize({ width, height });

    let cancelled = false;
    canvas.toBlob(result => {
      if (!result || cancelled) return;
      const nextUrl = URL.createObjectURL(result);
      setCropPreviewUrl(previous => {
        if (previous) URL.revokeObjectURL(previous);
        return nextUrl;
      });
    }, 'image/png');

    return () => {
      cancelled = true;
    };
  }, [
    aspectRatioId,
    cropRect,
    useSmartCrop,
    isCropRectDragging,
    cropSourceLoadedKey,
  ]);

  /** 有 objectUrl 時預先載入圖片取得尺寸，使輸出畫布區能顯示當前圖片大小（無需先打開裁切區） */
  useEffect(() => {
    if (!objectUrl) return;
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (!cancelled)
        setOriginalSize({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      if (!cancelled) setOriginalSize(null);
    };
    img.src = objectUrl;
    return () => {
      cancelled = true;
      img.src = '';
    };
  }, [objectUrl]);

  /** 「原圖大小」時，畫布寬高跟隨當前圖片尺寸；切回原圖大小時同步一次 */
  useEffect(() => {
    if (
      canvasSizeMode !== 'sameAsImage' ||
      !minOutputCanvasSize?.width ||
      !minOutputCanvasSize?.height
    )
      return;
    setOutputWidth(String(minOutputCanvasSize.width));
    setOutputHeight(String(minOutputCanvasSize.height));
    setCommittedOutputWidth(minOutputCanvasSize.width);
    setCommittedOutputHeight(minOutputCanvasSize.height);
  }, [canvasSizeMode, minOutputCanvasSize?.width, minOutputCanvasSize?.height]);

  useEffect(
    () => () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    },
    [objectUrl]
  );

  useEffect(
    () => () => {
      if (noBackgroundUrl) URL.revokeObjectURL(noBackgroundUrl);
    },
    [noBackgroundUrl]
  );

  useEffect(
    () => () => {
      if (upscaledUrl) URL.revokeObjectURL(upscaledUrl);
    },
    [upscaledUrl]
  );

  /** 僅在「長寬比」或「原圖尺寸」變更時更新裁切框為置中區域；重新打開裁切面板不重設範圍 */
  useEffect(() => {
    if (!originalSize?.width || !originalSize?.height) return;
    const ratioOption = ASPECT_RATIOS.find(r => r.id === aspectRatioId);
    const w = originalSize.width;
    const h = originalSize.height;
    if (!ratioOption || ratioOption.ratio == null || ratioOption.ratio <= 0)
      return;
    const [sx, sy, sw, sh] = getCropRegion(w, h, ratioOption.ratio);
    setCropRect({
      x: sx / w,
      y: sy / h,
      width: sw / w,
      height: sh / h,
    });
  }, [aspectRatioId, originalSize?.width, originalSize?.height]);

  /** 選擇預設長寬比時關閉「自由鎖定」，避免與預設比例衝突 */
  useEffect(() => {
    if (aspectRatioId !== 'original') {
      setCropFreeAspectRatioLocked(false);
      freeCropLockRatioRef.current = null;
    }
  }, [aspectRatioId]);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      const state = cropDragStateRef.current;
      const img = imgRef.current;
      if (!state || !img) return;

      const rect = img.getBoundingClientRect();
      const nw = img.naturalWidth;
      const nh = img.naturalHeight;
      if (!rect.width || !rect.height || !state.startRect || !nw || !nh) return;

      const deltaX = (event.clientX - state.startX) / rect.width;
      const deltaY = (event.clientY - state.startY) / rect.height;

      const nextRect: CropRect = { ...state.startRect };
      const minSize = 0.05;

      if (state.mode === 'move') {
        nextRect.x += deltaX;
        nextRect.y += deltaY;
      } else if (state.mode === 'nw') {
        nextRect.x += deltaX;
        nextRect.y += deltaY;
        nextRect.width -= deltaX;
        nextRect.height -= deltaY;
      } else if (state.mode === 'ne') {
        nextRect.y += deltaY;
        nextRect.width += deltaX;
        nextRect.height -= deltaY;
      } else if (state.mode === 'sw') {
        nextRect.x += deltaX;
        nextRect.width -= deltaX;
        nextRect.height += deltaY;
      } else if (state.mode === 'se') {
        nextRect.width += deltaX;
        nextRect.height += deltaY;
      }

      // 自由鎖定優先，其次預設長寬比；拖曳角落時維持寬／高像素比
      const presetRatio = currentRatioRef.current?.ratio ?? null;
      const ratioForResize =
        state.freeLockRatio != null && state.freeLockRatio > 0
          ? state.freeLockRatio
          : presetRatio != null && presetRatio > 0
            ? presetRatio
            : null;
      if (ratioForResize != null && state.mode !== 'move') {
        let widthNormalized = nextRect.width;
        if (widthNormalized < minSize) widthNormalized = minSize;
        if (widthNormalized > 1) widthNormalized = 1;
        let heightNormalized = normalizedCropHeightForWidthHeightRatio(
          widthNormalized,
          ratioForResize,
          nw,
          nh
        );
        if (heightNormalized > 1) {
          heightNormalized = 1;
          widthNormalized = normalizedCropWidthForWidthHeightRatio(
            heightNormalized,
            ratioForResize,
            nw,
            nh
          );
        }

        nextRect.width = widthNormalized;
        nextRect.height = heightNormalized;
      }

      // 寬高與位置一律限制在 0~1 範圍內，避免裁切尺寸大於圖片
      if (nextRect.width < minSize) nextRect.width = minSize;
      if (nextRect.height < minSize) nextRect.height = minSize;
      if (nextRect.width > 1) nextRect.width = 1;
      if (nextRect.height > 1) nextRect.height = 1;

      if (nextRect.x < 0) nextRect.x = 0;
      if (nextRect.y < 0) nextRect.y = 0;
      if (nextRect.x + nextRect.width > 1) nextRect.x = 1 - nextRect.width;
      if (nextRect.y + nextRect.height > 1) nextRect.y = 1 - nextRect.height;

      setCropRect(nextRect);
    };

    const handleMouseUp = () => {
      cropDragStateRef.current = null;
      setIsCropRectDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const applyFile = useCallback((next: File | null) => {
    setError(null);

    if (next) {
      if (!next.type.startsWith('image/')) {
        setError('請選擇圖片檔案');
        setFile(null);
        setObjectUrl(previous => {
          if (previous) URL.revokeObjectURL(previous);
          return null;
        });
        return;
      }

      setOriginalSize(null);
      setNoBackgroundUrl(previous => {
        if (previous) URL.revokeObjectURL(previous);
        return null;
      });
      setPreviewUpscaleFactor(1);
      setUpscaledUrl(previous => {
        if (previous) URL.revokeObjectURL(previous);
        return null;
      });
      setCropPreviewUrl(previous => {
        if (previous) URL.revokeObjectURL(previous);
        return null;
      });
      setCropPreviewSize(null);
      setCropPreviewUrl(previous => {
        if (previous) URL.revokeObjectURL(previous);
        return null;
      });
      setObjectUrl(previous => {
        if (previous) URL.revokeObjectURL(previous);
        return URL.createObjectURL(next);
      });
      setFile(next);
    } else {
      setFile(null);
      setOriginalSize(null);
      setPreviewUpscaleFactor(1);
      setNoBackgroundUrl(previous => {
        if (previous) URL.revokeObjectURL(previous);
        return null;
      });
      setUpscaledUrl(previous => {
        if (previous) URL.revokeObjectURL(previous);
        return null;
      });
      setObjectUrl(previous => {
        if (previous) URL.revokeObjectURL(previous);
        return null;
      });
    }

    setIsCroppingMode(false);
    setCropFreeAspectRatioLocked(false);
    freeCropLockRatioRef.current = null;
    setCropRect(null);
  }, []);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const list = Array.from(event.target.files ?? []).filter(f =>
        f.type.startsWith('image/')
      );
      if (list.length === 0) {
        setFiles([]);
        setCurrentFileIndexState(0);
        applyFile(null);
        return;
      }
      setFiles(list);
      setCurrentFileIndexState(0);
      applyFile(list[0] ?? null);
    },
    [applyFile]
  );

  const filesRef = useRef<File[]>([]);
  filesRef.current = files;
  const setCurrentFileIndex = useCallback(
    (index: number) => {
      setCurrentFileIndexState(index);
      const list = filesRef.current;
      applyFile(list[index] ?? null);
    },
    [applyFile]
  );

  const removeFileAt = useCallback(
    (index: number) => {
      const list = filesRef.current;
      if (index < 0 || index >= list.length) return;
      const newFiles = list.filter((_, i) => i !== index);
      if (newFiles.length === 0) {
        setFiles([]);
        setCurrentFileIndexState(0);
        applyFile(null);
        return;
      }
      setFiles(newFiles);
      let newIndex = currentFileIndex;
      if (index === currentFileIndex) {
        newIndex = Math.min(currentFileIndex, newFiles.length - 1);
      } else if (index < currentFileIndex) {
        newIndex = currentFileIndex - 1;
      }
      setCurrentFileIndexState(newIndex);
      applyFile(newFiles[newIndex] ?? null);
    },
    [applyFile, currentFileIndex]
  );

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(false);
      const list = Array.from(event.dataTransfer.files ?? []).filter(f =>
        f.type.startsWith('image/')
      );
      if (list.length === 0) {
        setFiles([]);
        setCurrentFileIndexState(0);
        applyFile(null);
        return;
      }
      setFiles(list);
      setCurrentFileIndexState(0);
      applyFile(list[0] ?? null);
    },
    [applyFile]
  );

  const handleCropMouseDown = useCallback(
    (
      event: React.MouseEvent<HTMLDivElement>,
      mode: 'move' | 'nw' | 'ne' | 'sw' | 'se'
    ) => {
      if (!isCroppingMode || !cropRect) return;
      event.preventDefault();
      event.stopPropagation();
      setIsCropRectDragging(true);
      const imgEl = imgRef.current;
      const nw = imgEl?.naturalWidth ?? 0;
      const nh = imgEl?.naturalHeight ?? 0;
      const lockR = freeCropLockRatioRef.current;
      const freeLockRatio =
        aspectRatioId === 'original' &&
        cropFreeAspectRatioLocked &&
        lockR != null &&
        lockR > 0 &&
        nw > 0 &&
        nh > 0
          ? lockR
          : null;
      cropDragStateRef.current = {
        mode,
        startX: event.clientX,
        startY: event.clientY,
        startRect: { ...cropRect },
        freeLockRatio,
      };
    },
    [aspectRatioId, cropFreeAspectRatioLocked, cropRect, isCroppingMode]
  );

  const handleToggleCropMode = useCallback(() => {
    if (!file || !objectUrl) return;
    setIsCroppingMode(previous => !previous);
    if (!cropRect && originalSize?.width && originalSize?.height) {
      const ratioOption = ASPECT_RATIOS.find(r => r.id === aspectRatioId);
      if (ratioOption?.ratio != null && ratioOption.ratio > 0) {
        const [sx, sy, sw, sh] = getCropRegion(
          originalSize.width,
          originalSize.height,
          ratioOption.ratio
        );
        setCropRect({
          x: sx / originalSize.width,
          y: sy / originalSize.height,
          width: sw / originalSize.width,
          height: sh / originalSize.height,
        });
      } else {
        setCropRect({ x: 0.1, y: 0.1, width: 0.8, height: 0.8 });
      }
    } else if (!cropRect) {
      setCropRect({ x: 0.1, y: 0.1, width: 0.8, height: 0.8 });
    }
  }, [
    aspectRatioId,
    cropRect,
    file,
    objectUrl,
    originalSize?.width,
    originalSize?.height,
  ]);

  const handleResetCrop = useCallback(() => {
    // 回復至預設狀態：不指定長寬比、不使用智能裁切，裁切區覆蓋整張圖。
    setAspectRatioId('original');
    setUseSmartCrop(false);
    setCropFreeAspectRatioLocked(false);
    freeCropLockRatioRef.current = null;
    setCropRect({ x: 0, y: 0, width: 1, height: 1 });
    cropDragStateRef.current = null;
  }, []);

  const toggleFreeCropAspectLock = useCallback(() => {
    const img = imgRef.current;
    setCropFreeAspectRatioLocked(prev => {
      if (prev) {
        freeCropLockRatioRef.current = null;
        return false;
      }
      if (!cropRect || !img?.naturalWidth || !img?.naturalHeight) return false;
      const wPx = cropRect.width * img.naturalWidth;
      const hPx = cropRect.height * img.naturalHeight;
      if (wPx < MIN_PX || hPx < MIN_PX) return false;
      freeCropLockRatioRef.current = wPx / hPx;
      return true;
    });
  }, [cropRect]);

  const handleResetBorder = useCallback(() => {
    setBorderShape('rect');
    setBorderCornerRadius(0);
    setBorderWidth(0);
    setBorderColor('#000000');
  }, []);

  const setIconPaddingState = useCallback((value: number) => {
    setIconPadding(Math.max(0, Math.min(80, value)));
  }, []);

  const addIconItem = useCallback((item: Omit<IconBlock, 'id'>) => {
    nextIconIdRef.current += 1;
    const id = `icon-${nextIconIdRef.current}`;
    setIconItems(prev => [...prev, { ...item, id }]);
  }, []);

  const removeIconItem = useCallback((id: string) => {
    setIconItems(prev => prev.filter(item => item.id !== id));
  }, []);

  const updateIconItem = useCallback(
    (id: string, patch: Partial<Omit<IconBlock, 'id'>>) => {
      setIconItems(prev =>
        prev.map(item => (item.id === id ? { ...item, ...patch } : item))
      );
    },
    []
  );

  const handleResetIcons = useCallback(() => {
    setIconItems([]);
    nextIconIdRef.current = 0;
  }, []);

  const handleResetWatermark = useCallback(() => {
    nextWatermarkIdRef.current = 0;
    setWatermarkItems([
      {
        id: 'w-0',
        text: '',
        fontFamily: 'sans-serif',
        fontSize: 24,
        color: '#000000',
        opacity: 0.7,
      },
    ]);
  }, []);

  const addWatermarkItem = useCallback(() => {
    nextWatermarkIdRef.current += 1;
    const id = `w-${nextWatermarkIdRef.current}`;
    setWatermarkItems(prev => [
      ...prev,
      {
        id,
        text: '',
        fontFamily: 'sans-serif',
        fontSize: 24,
        color: '#000000',
        opacity: 0.7,
        anchorX: 0.5,
        anchorY: 0.5,
      },
    ]);
  }, []);

  const removeWatermarkItem = useCallback((id: string) => {
    setWatermarkItems(prev => {
      const next = prev.filter(item => item.id !== id);
      return next.length > 0
        ? next
        : [
            {
              id: 'w-0',
              text: '',
              fontFamily: 'sans-serif',
              fontSize: 24,
              color: '#000000',
              opacity: 0.7,
              anchorX: 0.5,
              anchorY: 0.5,
            },
          ];
    });
  }, []);

  const updateWatermarkItem = useCallback(
    (id: string, patch: Partial<Omit<WatermarkBlock, 'id'>>) => {
      setWatermarkItems(prev =>
        prev.map(item => (item.id === id ? { ...item, ...patch } : item))
      );
    },
    []
  );

  const addQrCodeItem = useCallback(() => {
    setQrCodeItems(prev => [
      ...prev,
      {
        id: `qr-${Date.now()}`,
        content: '',
        size: 80,
        anchorX: 0.5,
        anchorY: 0.5,
        darkColor: '#000000',
        lightColor: '#ffffff',
      },
    ]);
  }, []);

  const removeQrCodeItem = useCallback((id: string) => {
    setQrCodeItems(prev => prev.filter(item => item.id !== id));
    setQrCodePreviewUrls(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const updateQrCodeItem = useCallback(
    (id: string, patch: Partial<Omit<QRCodeBlock, 'id'>>) => {
      setQrCodeItems(prev =>
        prev.map(item => (item.id !== id ? item : { ...item, ...patch }))
      );
    },
    []
  );

  const handleResetQrCode = useCallback(() => {
    setQrCodeItems([]);
    setQrCodePreviewUrls({});
  }, []);

  useEffect(() => {
    const itemsWithContent = qrCodeItems.filter(item => item.content.trim());
    if (itemsWithContent.length === 0) {
      setQrCodePreviewUrls({});
      return;
    }
    let cancelled = false;
    const QRCodePromise = import('qrcode').then(m => m.default);
    const next: Record<string, string> = {};
    const run = async () => {
      for (const item of itemsWithContent) {
        if (cancelled) return;
        const size = clampIconQrSizeMin(item.size);
        try {
          const QRCode = await QRCodePromise;
          const dark = (item.darkColor ?? '#000000').trim() || '#000000';
          const light = (item.lightColor ?? '#ffffff').trim() || '#ffffff';
          const url = await QRCode.toDataURL(item.content.trim(), {
            width: size,
            margin: 1,
            color: { dark, light },
          });
          if (!cancelled) next[item.id] = url;
        } catch {
          if (!cancelled) delete next[item.id];
        }
      }
      if (!cancelled) setQrCodePreviewUrls(next);
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [qrCodeItems]);

  const handleCropWidthPxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value.replace(/\D/g, '');
      if (!cropRect || !raw) return;

      const imageElement = imgRef.current;
      const baseWidth = originalSize?.width ?? imageElement?.naturalWidth ?? 0;
      const baseHeight =
        originalSize?.height ?? imageElement?.naturalHeight ?? 0;
      if (!baseWidth || !baseHeight) return;

      const pixels = Number(raw);
      if (!Number.isFinite(pixels)) return;
      const clampedPixels = Math.min(Math.max(pixels, MIN_PX), baseWidth);
      const nextWidthNormalized = clampedPixels / baseWidth;

      setCropRect(previous => {
        if (!previous) return previous;
        let widthNormalized = nextWidthNormalized;
        let heightNormalized = previous.height;
        let xNormalized = previous.x;
        let yNormalized = previous.y;

        const presetR = currentRatio?.ratio ?? null;
        const lockR =
          aspectRatioId === 'original' && cropFreeAspectRatioLocked
            ? freeCropLockRatioRef.current
            : null;
        const ratio =
          presetR && presetR > 0
            ? presetR
            : lockR != null && lockR > 0
              ? lockR
              : null;
        if (ratio && ratio > 0) {
          heightNormalized = normalizedCropHeightForWidthHeightRatio(
            widthNormalized,
            ratio,
            baseWidth,
            baseHeight
          );
        }

        if (widthNormalized <= 0) widthNormalized = MIN_PX / baseWidth;
        if (heightNormalized <= 0) heightNormalized = MIN_PX / baseHeight;

        if (xNormalized + widthNormalized > 1) {
          xNormalized = Math.max(0, 1 - widthNormalized);
        }
        if (yNormalized + heightNormalized > 1) {
          yNormalized = Math.max(0, 1 - heightNormalized);
        }

        return {
          ...previous,
          x: xNormalized,
          y: yNormalized,
          width: widthNormalized,
          height: heightNormalized,
        };
      });
    },
    [
      aspectRatioId,
      cropFreeAspectRatioLocked,
      cropRect,
      currentRatio?.ratio,
      originalSize,
    ]
  );

  const handleCropHeightPxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value.replace(/\D/g, '');
      if (!cropRect || !raw) return;

      const imageElement = imgRef.current;
      const baseWidth = originalSize?.width ?? imageElement?.naturalWidth ?? 0;
      const baseHeight =
        originalSize?.height ?? imageElement?.naturalHeight ?? 0;
      if (!baseWidth || !baseHeight) return;

      const pixels = Number(raw);
      if (!Number.isFinite(pixels)) return;
      const clampedPixels = Math.min(Math.max(pixels, MIN_PX), baseHeight);
      const nextHeightNormalized = clampedPixels / baseHeight;

      setCropRect(previous => {
        if (!previous) return previous;
        let heightNormalized = nextHeightNormalized;
        let widthNormalized = previous.width;
        let yNormalized = previous.y;
        let xNormalized = previous.x;

        const presetR = currentRatio?.ratio ?? null;
        const lockR =
          aspectRatioId === 'original' && cropFreeAspectRatioLocked
            ? freeCropLockRatioRef.current
            : null;
        const ratio =
          presetR && presetR > 0
            ? presetR
            : lockR != null && lockR > 0
              ? lockR
              : null;
        if (ratio && ratio > 0) {
          widthNormalized = normalizedCropWidthForWidthHeightRatio(
            heightNormalized,
            ratio,
            baseWidth,
            baseHeight
          );
        }

        if (heightNormalized <= 0) heightNormalized = MIN_PX / baseHeight;
        if (widthNormalized <= 0) widthNormalized = MIN_PX / baseWidth;

        if (yNormalized + heightNormalized > 1) {
          yNormalized = Math.max(0, 1 - heightNormalized);
        }
        if (xNormalized + widthNormalized > 1) {
          xNormalized = Math.max(0, 1 - widthNormalized);
        }

        return {
          ...previous,
          x: xNormalized,
          y: yNormalized,
          width: widthNormalized,
          height: heightNormalized,
        };
      });
    },
    [
      aspectRatioId,
      cropFreeAspectRatioLocked,
      cropRect,
      currentRatio?.ratio,
      originalSize,
    ]
  );

  const handleApplyCrop = useCallback(async () => {
    if (!file || !cropRect) return;
    // 套用僅代表「記住目前裁切設定」，實際圖片仍保留為原圖，
    // 並將裁切區塊收合，後續仍可再次展開並沿用同一組設定。
    setError(null);
    setIsCroppingMode(false);
    setCropApplying(false);
  }, [cropRect, file]);

  const handleRemoveBackground = useCallback(async () => {
    if (!file) return;

    setError(null);
    setRemoveBackgroundLoading(true);

    try {
      const { removeBackground } = await import('@imgly/background-removal');
      const isPngOrJpeg =
        file.type === 'image/png' || file.type === 'image/jpeg';

      const inputBlob: Blob =
        isPngOrJpeg || !file.type
          ? file
          : await (async () =>
              new Promise<Blob>((resolve, reject) => {
                const url = URL.createObjectURL(file);
                const image = new Image();

                image.onload = () => {
                  URL.revokeObjectURL(url);
                  const canvas = document.createElement('canvas');
                  canvas.width = image.naturalWidth;
                  canvas.height = image.naturalHeight;

                  const context = canvas.getContext('2d');
                  if (!context) {
                    reject(new Error('無法建立畫布'));
                    return;
                  }

                  context.drawImage(image, 0, 0);
                  canvas.toBlob(
                    blob =>
                      blob ? resolve(blob) : reject(new Error('轉檔失敗')),
                    'image/png',
                    1
                  );
                };

                image.onerror = () => {
                  URL.revokeObjectURL(url);
                  reject(new Error('圖片載入失敗'));
                };

                image.src = url;
              }))();

      const blob = await removeBackground(inputBlob, { device: 'cpu' });
      setNoBackgroundUrl(previous => {
        if (previous) URL.revokeObjectURL(previous);
        return URL.createObjectURL(blob);
      });
    } catch (unknownError) {
      const message =
        unknownError instanceof Error
          ? unknownError.message
          : '去背失敗，請稍後再試或換一張圖片';
      setError(message.startsWith('去背') ? message : `去背失敗：${message}`);
    } finally {
      setRemoveBackgroundLoading(false);
    }
  }, [file]);

  const handleClearNoBackground = useCallback(() => {
    setNoBackgroundUrl(previous => {
      if (previous) URL.revokeObjectURL(previous);
      return null;
    });
  }, []);

  const handleOutput = useCallback(
    async (options?: { pipelineCropRect?: CropRect | null }) => {
      const outputFiles = files.length > 1 ? files : file ? [file] : [];
      if (outputFiles.length === 0 || !currentFormat) return;

      setProcessing(true);
      setError(null);
      setPipelineCompletedSteps([]);
      setPipelineCurrentStep('準備中…');

      const isBatch = outputFiles.length > 1;
      /** 去背：單張與批次皆套用（批次時每張都會跑去背，較耗時） */
      const pipelineRemoveBg = !!noBackgroundUrl;
      const cropModified =
        !!cropRect &&
        (cropRect.x !== 0 ||
          cropRect.y !== 0 ||
          cropRect.width !== 1 ||
          cropRect.height !== 1 ||
          aspectRatioId !== 'original' ||
          useSmartCrop);
      const pipelineAspectRatio =
        cropModified && currentRatio?.ratio != null ? currentRatio.ratio : null;
      /** 放大：單張與批次皆套用 */
      const pipelineUpscale = !!upscaledUrl;
      const pipelineUpscaleFactor = upscaledUrl ? previewUpscaleFactor : 2;
      const canvasModified =
        canvasSizeMode === 'custom' &&
        committedOutputWidth > 0 &&
        committedOutputHeight > 0 &&
        minOutputCanvasSize != null &&
        (committedOutputWidth !== minOutputCanvasSize.width ||
          committedOutputHeight !== minOutputCanvasSize.height ||
          outputBackgroundColor !== '#ffffff' ||
          (outputBackgroundType === 'image' && !!outputBackgroundImage) ||
          canvasAnchorX !== 0.5 ||
          canvasAnchorY !== 0.5);
      /** 原圖大小：不傳 outputSize，不套用任何畫布設定；自訂時才傳畫布寬高與背景 */
      const outputWidthValue =
        canvasSizeMode === 'sameAsImage'
          ? 0
          : canvasModified
            ? effectiveOutputWidth
            : (minOutputCanvasSize?.width ?? 0);
      const outputHeightValue =
        canvasSizeMode === 'sameAsImage'
          ? 0
          : canvasModified
            ? effectiveOutputHeight
            : (minOutputCanvasSize?.height ?? 0);

      const formatWithQuality =
        currentFormat.mime === 'image/jpeg' ||
        currentFormat.mime === 'image/webp'
          ? { ...currentFormat, quality: compressionRatio }
          : currentFormat;

      /** 裁切區：單張與批次皆套用同一正規化區塊（批次時每張套用相同相對區塊） */
      const pipelineCropRect = options?.pipelineCropRect ?? null;

      const pipelineOptions = {
        enableRemoveBackground: pipelineRemoveBg,
        removeBackgroundOptions: { device: 'cpu' as const },
        cropRect: pipelineCropRect,
        aspectRatio: pipelineAspectRatio,
        useSmartCrop: pipelineAspectRatio != null ? useSmartCrop : false,
        enableUpscale: pipelineUpscale,
        upscaleFactor: pipelineUpscaleFactor,
        outputSize:
          outputWidthValue || outputHeightValue
            ? {
                width: outputWidthValue || undefined,
                height: outputHeightValue || undefined,
                allowPadding: true,
                /** 預設白底不填色，保留透明（上傳去背 PNG 或本工具去背）；僅在使用者改過背景色時才填滿 */
                backgroundColor:
                  outputBackgroundType === 'color' &&
                  outputBackgroundColor &&
                  outputBackgroundColor !== '#ffffff'
                    ? outputBackgroundColor
                    : undefined,
                backgroundImage:
                  outputBackgroundType === 'image' && outputBackgroundImage
                    ? outputBackgroundImage
                    : undefined,
                anchorX: canvasAnchorX,
                anchorY: canvasAnchorY,
              }
            : undefined,
        outputScaleRatio,
        targetOutputPixels: targetOutputPixels ?? undefined,
        adjustments:
          brightness || contrast || saturation
            ? {
                brightness: brightness / 100,
                contrast: contrast / 100,
                saturation: saturation / 100,
              }
            : undefined,
        border:
          borderShape !== 'rect' || borderCornerRadius > 0 || borderWidth > 0
            ? {
                shape: borderShape,
                cornerRadius: Math.max(0, borderCornerRadius),
                borderWidth: Math.max(0, borderWidth),
                borderColor: borderColor.trim() || '#000000',
              }
            : undefined,
        icons:
          iconItems.length > 0
            ? {
                items: iconItems.map(item => ({
                  source: item.source,
                  iconId: item.iconId,
                  imageDataUrl: item.imageDataUrl,
                  size: clampIconQrSizeMin(item.size),
                  color: item.color?.trim() || '#000000',
                  opacity: Math.min(1, Math.max(0, item.opacity)),
                  anchorX: item.anchorX ?? 0.5,
                  anchorY: item.anchorY ?? 0.5,
                  shape: item.shape ?? 'none',
                })),
                padding: Math.max(0, iconPadding),
              }
            : undefined,
        watermark: watermarkItems.some(item => item.text?.trim())
          ? {
              items: watermarkItems.map(item => ({
                text: item.text,
                fontFamily: item.fontFamily?.trim() || 'sans-serif',
                fontSize: clampWatermarkFontSizeMin(item.fontSize),
                color: item.color?.trim() || '#000000',
                opacity: Math.min(1, Math.max(0, item.opacity)),
                anchorX: item.anchorX ?? 0.5,
                anchorY: item.anchorY ?? 0.5,
              })),
              padding: 0,
            }
          : undefined,
        qrcode:
          qrCodeItems.filter(i => i.content.trim()).length > 0
            ? {
                items: qrCodeItems
                  .filter(i => i.content.trim())
                  .map(item => ({
                    content: item.content.trim(),
                    size: clampIconQrSizeMin(item.size),
                    anchorX: item.anchorX,
                    anchorY: item.anchorY,
                    darkColor:
                      (item.darkColor ?? '#000000').trim() || '#000000',
                    lightColor:
                      (item.lightColor ?? '#ffffff').trim() || '#ffffff',
                  })),
              }
            : undefined,
        format: formatWithQuality,
      };

      try {
        if (isBatch) {
          const JSZip = (await import('jszip')).default;
          const zip = new JSZip();
          const total = outputFiles.length;
          for (let i = 0; i < total; i += 1) {
            setPipelineCurrentStep(`處理第 ${i + 1}/${total} 張…`);
            const { blob } = await runImagePipeline(
              outputFiles[i]!,
              pipelineOptions,
              progress => {
                const label =
                  progress.currentStep == null
                    ? null
                    : PIPELINE_STEP_LABELS[progress.currentStep];
                setPipelineCurrentStep(
                  label
                    ? `第 ${i + 1}/${total} 張：${label}`
                    : `處理第 ${i + 1}/${total} 張…`
                );
              }
            );
            const base =
              outputFiles[i]!.name.replace(/\.[^.]+$/, '') || 'image';
            const ext = currentFormat.ext;
            zip.file(`${base}_${i + 1}.${ext}`, blob);
          }
          setPipelineCurrentStep('壓縮中…');
          const zipBlob = await zip.generateAsync({
            type: 'blob',
            compression: 'DEFLATE',
            compressionOptions: { level: 6 },
          });
          const url = URL.createObjectURL(zipBlob);
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.download = `images_${total}_${Date.now()}.zip`;
          anchor.click();
          URL.revokeObjectURL(url);
          setPipelineCurrentStep(null);
          setProcessing(false);
          return;
        }

        const { blob } = await runImagePipeline(
          file!,
          pipelineOptions,
          progress => {
            const currentStepLabel =
              progress.currentStep == null
                ? null
                : PIPELINE_STEP_LABELS[progress.currentStep];

            setPipelineCurrentStep(currentStepLabel);
            setPipelineCompletedSteps(
              progress.completedSteps.map(
                stepId => PIPELINE_STEP_LABELS[stepId]
              )
            );
          }
        );

        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `image.${currentFormat.ext}`;
        anchor.click();
        URL.revokeObjectURL(url);

        setPipelineCurrentStep(null);
        setProcessing(false);
      } catch (unknownError) {
        setPipelineCurrentStep(null);
        setPipelineCompletedSteps([]);
        setProcessing(false);
        setError(
          unknownError instanceof Error
            ? unknownError.message
            : '處理失敗，請稍後再試'
        );
      }
    },
    [
      compressionRatio,
      outputScaleRatio,
      currentFormat,
      currentRatio,
      noBackgroundUrl,
      cropRect,
      aspectRatioId,
      useSmartCrop,
      upscaledUrl,
      previewUpscaleFactor,
      canvasSizeMode,
      committedOutputWidth,
      committedOutputHeight,
      minOutputCanvasSize?.width,
      minOutputCanvasSize?.height,
      outputBackgroundType,
      outputBackgroundImage,
      outputBackgroundColor,
      canvasAnchorX,
      canvasAnchorY,
      files,
      file,
      brightness,
      contrast,
      saturation,
      borderShape,
      borderCornerRadius,
      borderWidth,
      borderColor,
      iconItems,
      iconPadding,
      watermarkItems,
      qrCodeItems,
      targetOutputPixels,
    ]
  );

  const handleNineGridSlice = useCallback(async () => {
    if (gridSliceCount === null) return;
    const fileList = files.length > 0 ? files : file ? [file] : [];
    if (fileList.length === 0) {
      setError('請先選擇圖片');
      return;
    }
    const n = gridSliceCount === 9 ? 3 : gridSliceCount === 16 ? 4 : 5;
    try {
      setProcessing(true);
      setPipelineCompletedSteps([]);
      setPipelineCurrentStep('產生網格切割中…');
      setError(null);

      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      const ext = currentFormat.ext;

      for (let fileIndex = 0; fileIndex < fileList.length; fileIndex += 1) {
        const f = fileList[fileIndex]!;
        setPipelineCurrentStep(
          fileList.length > 1
            ? `處理第 ${fileIndex + 1}/${fileList.length} 張…`
            : '產生網格切割中…'
        );

        const { blob } = await runImagePipeline(f, {
          enableRemoveBackground: false,
          enableUpscale: false,
          upscaleFactor: 1,
          aspectRatio: null,
          useSmartCrop: false,
          cropRect: null,
          adjustments: undefined,
          border: null,
          icons: null,
          watermark: null,
          qrcode: null,
          outputSize: undefined,
          outputScaleRatio: 1,
          targetOutputPixels:
            estimatedOutputSize ?? minOutputCanvasSize ?? undefined,
          format: currentFormat,
        });

        const baseCanvas = await loadBlobToCanvas(blob);
        const cellWidth = Math.floor(baseCanvas.width / n);
        const cellHeight = Math.floor(baseCanvas.height / n);
        const base = f.name.replace(/\.[^.]+$/, '') || 'image';
        const useFolder = fileList.length > 1;
        const zipTarget = useFolder ? (zip.folder(base) ?? zip) : zip;

        for (let row = 0; row < n; row += 1) {
          for (let col = 0; col < n; col += 1) {
            const cellCanvas = document.createElement('canvas');
            cellCanvas.width = cellWidth;
            cellCanvas.height = cellHeight;
            const ctx = cellCanvas.getContext('2d');
            if (!ctx) continue;
            ctx.drawImage(
              baseCanvas,
              col * cellWidth,
              row * cellHeight,
              cellWidth,
              cellHeight,
              0,
              0,
              cellWidth,
              cellHeight
            );
            const cellBlob = await canvasToBlob(
              cellCanvas,
              currentFormat.mime,
              currentFormat.quality ?? undefined
            );
            const fileName = `${row + 1}-${col + 1}.${ext}`;
            zipTarget.file(
              useFolder ? fileName : `${base}_${fileName}`,
              cellBlob
            );
          }
        }
      }

      setPipelineCurrentStep('壓縮 ZIP 中…');
      const zipBlob = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 },
      });
      const url = URL.createObjectURL(zipBlob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `grid-slice_${gridSliceCount}_${Date.now()}.zip`;
      anchor.click();
      URL.revokeObjectURL(url);

      setPipelineCurrentStep(null);
      setProcessing(false);
    } catch (unknownError) {
      setPipelineCurrentStep(null);
      setProcessing(false);
      setError(
        unknownError instanceof Error
          ? unknownError.message
          : '網格切割失敗，請稍後再試'
      );
    }
  }, [
    file,
    files,
    gridSliceCount,
    estimatedOutputSize,
    minOutputCanvasSize,
    outputAllowPadding,
    outputBackgroundType,
    outputBackgroundColor,
    outputBackgroundImage,
    canvasAnchorX,
    canvasAnchorY,
    currentFormat,
  ]);

  const handleMergeImages = useCallback(async () => {
    if (files.length < 2) {
      setError('請至少選擇 2 張圖片再合併');
      return;
    }
    try {
      setProcessing(true);
      setPipelineCurrentStep('合併多圖中…');
      setError(null);

      const objectUrls: string[] = [];
      for (const f of files) {
        objectUrls.push(URL.createObjectURL(f));
      }
      try {
        const loaded: { img: HTMLImageElement; w: number; h: number }[] = [];
        for (const url of objectUrls) {
          const img = await new Promise<HTMLImageElement>((resolve, reject) => {
            const el = new Image();
            el.onload = () => resolve(el);
            el.onerror = () => reject(new Error('圖片載入失敗'));
            el.src = url;
          });
          loaded.push({
            img,
            w: img.naturalWidth,
            h: img.naturalHeight,
          });
        }

        const n = loaded.length;
        const mergeFormat =
          currentFormat.mime === 'image/jpeg' ? 'image/jpeg' : 'image/png';
        const quality = currentFormat.quality ?? 0.92;
        const mergeRatioOption = MERGE_ASPECT_RATIOS.find(
          r => r.id === mergeAspectRatioId
        );
        const mergeRatio = mergeRatioOption?.ratio ?? null;

        let contentW: number;
        let contentH: number;
        let dataUri: string;

        if (mergeLayoutId === 'vertical') {
          const totalHeight = loaded.reduce((sum, x) => sum + x.h, 0);
          const maxWidth = Math.max(...loaded.map(x => x.w), 1);
          let y = 0;
          const sources: { src: string; x: number; y: number }[] = [];
          for (let i = 0; i < objectUrls.length; i += 1) {
            const centerX = (maxWidth - loaded[i]!.w) / 2;
            sources.push({
              src: objectUrls[i]!,
              x: Math.max(0, Math.round(centerX)),
              y,
            });
            y += loaded[i]!.h;
          }
          const { default: mergeImages } = await import('merge-images');
          dataUri = await mergeImages(sources, {
            width: maxWidth,
            height: totalHeight,
            format: mergeFormat,
            quality,
          });
          contentW = maxWidth;
          contentH = totalHeight;
        } else {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('無法建立畫布');

          if (mergeLayoutId === 'horizontal') {
            contentW = loaded.reduce((sum, x) => sum + x.w, 0);
            contentH = Math.max(...loaded.map(x => x.h), 1);
            canvas.width = contentW;
            canvas.height = contentH;
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, contentW, contentH);
            let x = 0;
            for (let i = 0; i < loaded.length; i += 1) {
              const { img, w, h } = loaded[i]!;
              const y = (contentH - h) / 2;
              ctx.drawImage(img, x, y, w, h);
              x += w;
            }
          } else {
            const cols = Math.ceil(Math.sqrt(n));
            const rows = Math.ceil(n / cols);
            const cellW = Math.max(...loaded.map(x => x.w), 1);
            const cellH = Math.max(...loaded.map(x => x.h), 1);
            contentW = cols * cellW;
            contentH = rows * cellH;
            canvas.width = contentW;
            canvas.height = contentH;
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, contentW, contentH);
            for (let i = 0; i < loaded.length; i += 1) {
              const { img, w, h } = loaded[i]!;
              const row = Math.floor(i / cols);
              const col = i % cols;
              const cellX = col * cellW;
              const cellY = row * cellH;
              const scale = Math.min(cellW / w, cellH / h, 1);
              const drawW = w * scale;
              const drawH = h * scale;
              const dx = cellX + (cellW - drawW) / 2;
              const dy = cellY + (cellH - drawH) / 2;
              ctx.drawImage(img, 0, 0, w, h, dx, dy, drawW, drawH);
            }
          }

          dataUri = canvas.toDataURL(mergeFormat, quality);
        }

        let blob: Blob;
        if (mergeRatio == null || mergeRatio <= 0) {
          const res = await fetch(dataUri);
          blob = await res.blob();
        } else {
          const canvasW = Math.max(contentW, Math.ceil(contentH * mergeRatio));
          const canvasH = Math.ceil(canvasW / mergeRatio);
          const offsetX = (canvasW - contentW) / 2;
          const offsetY = (canvasH - contentH) / 2;

          const img = await new Promise<HTMLImageElement>((resolve, reject) => {
            const el = new Image();
            el.onload = () => resolve(el);
            el.onerror = () => reject(new Error('合併圖載入失敗'));
            el.src = dataUri;
          });
          const canvas = document.createElement('canvas');
          canvas.width = canvasW;
          canvas.height = canvasH;
          const ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('無法建立畫布');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvasW, canvasH);
          ctx.drawImage(img, offsetX, offsetY, contentW, contentH);
          blob = await canvasToBlob(canvas, mergeFormat, quality);
        }

        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `merged_${Date.now()}.${mergeFormat === 'image/jpeg' ? 'jpg' : 'png'}`;
        anchor.click();
        URL.revokeObjectURL(url);
      } finally {
        for (const u of objectUrls) URL.revokeObjectURL(u);
      }

      setPipelineCurrentStep(null);
      setProcessing(false);
    } catch (unknownError) {
      setPipelineCurrentStep(null);
      setProcessing(false);
      setError(
        unknownError instanceof Error
          ? unknownError.message
          : '多圖合併失敗，請稍後再試'
      );
    }
  }, [files, currentFormat, mergeAspectRatioId, mergeLayoutId]);

  const handleUpscale = useCallback(
    async (scale: 2 | 4) => {
      const source = upscaledUrl ?? noBackgroundUrl ?? objectUrl;
      if (!source) return;

      setError(null);
      setUpscaleLoading(true);

      try {
        const Upscaler = (await import('upscaler')).default;
        const upscaler = new Upscaler();
        const steps = scale === 4 ? 2 : 1;
        let currentSource = source;

        for (let index = 0; index < steps; index += 1) {
          const upscaledSource = await upscaler.upscale(currentSource);
          const response = await fetch(upscaledSource);
          const blob = await response.blob();

          setUpscaledUrl(previous => {
            if (previous) URL.revokeObjectURL(previous);
            return URL.createObjectURL(blob);
          });
          setPreviewUpscaleFactor(scale);

          currentSource = URL.createObjectURL(blob);
        }
      } catch (unknownError) {
        setError(
          unknownError instanceof Error
            ? unknownError.message
            : 'AI 放大失敗，請稍後再試'
        );
      } finally {
        setUpscaleLoading(false);
      }
    },
    [noBackgroundUrl, objectUrl, upscaledUrl]
  );

  const handleClearUpscale = useCallback(() => {
    setPreviewUpscaleFactor(1);
    setUpscaledUrl(previous => {
      if (previous) URL.revokeObjectURL(previous);
      return null;
    });
  }, []);

  return {
    acceptImage: ACCEPT_IMAGE,
    maxPreview: MAX_PREVIEW,
    minPx: MIN_PX,
    maxPx: MAX_PX,
    aspectRatios: ASPECT_RATIOS,
    outputFormats: OUTPUT_FORMATS,
    file,
    files,
    currentFileIndex,
    setCurrentFileIndex,
    removeFileAt,
    objectUrl,
    originalSize,
    previewUrl,
    cropSourceUrl,
    noBackgroundUrl,
    upscaledUrl,
    isDragging,
    aspectRatioId,
    formatId,
    outputWidth,
    outputHeight,
    committedOutputWidth,
    committedOutputHeight,
    effectiveOutputWidth,
    effectiveOutputHeight,
    isCroppingMode,
    cropRect,
    cropApplying,
    processing,
    error,
    removeBackgroundLoading,
    upscaleLoading,
    upscaleFactor,
    useSmartCrop,
    brightness,
    contrast,
    saturation,
    borderShape,
    borderCornerRadius,
    borderWidth,
    borderColor,
    watermarkItems,
    qrCodeItems,
    qrCodePreviewUrls,
    addQrCodeItem,
    removeQrCodeItem,
    updateQrCodeItem,
    handleResetQrCode,
    outputAllowPadding,
    outputBackgroundType,
    outputBackgroundColor,
    outputBackgroundImage,
    setOutputBackgroundType,
    setOutputBackgroundColor,
    setOutputBackgroundImage,
    canvasAnchorX,
    canvasAnchorY,
    compressionRatio,
    outputScaleRatio,
    targetOutputPixels,
    estimatedOutputSize,
    canvasContentSize,
    minOutputCanvasSize,
    canvasSizeMode,
    setCanvasSizeMode,
    pipelineCurrentStep,
    pipelineCompletedSteps,
    imgRef,
    handleFileChange,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleToggleCropMode,
    handleResetCrop,
    handleCropMouseDown,
    handleCropWidthPxChange,
    handleCropHeightPxChange,
    toggleFreeCropAspectLock,
    cropFreeAspectRatioLocked,
    handleApplyCrop,
    handleRemoveBackground,
    handleClearNoBackground,
    handleUpscale,
    handleClearUpscale,
    handleOutput,
    setAspectRatioId,
    setFormatId,
    setOutputWidth,
    setOutputHeight,
    setUseSmartCrop,
    setUpscaleFactor,
    setBrightness,
    setContrast,
    setSaturation,
    setBorderShape,
    setBorderCornerRadius,
    setBorderWidth,
    setBorderColor,
    handleResetBorder,
    iconItems,
    iconPadding,
    setIconPadding: setIconPaddingState,
    addIconItem,
    removeIconItem,
    updateIconItem,
    handleResetIcons,
    addWatermarkItem,
    removeWatermarkItem,
    updateWatermarkItem,
    handleResetWatermark,
    setOriginalSize,
    setError,
    setOutputAllowPadding,
    setCanvasAnchor: ({ x, y }: { x: number; y: number }) => {
      setCanvasAnchorX(Math.min(Math.max(x, 0), 1));
      setCanvasAnchorY(Math.min(Math.max(y, 0), 1));
    },
    setCompressionRatio,
    setOutputScaleRatio,
    setTargetOutputPixels,
    clearTargetOutputPixels,
    commitOutputSize,
    applyCanvasAspectRatio,
    notifyCropSourceLoaded: useCallback(() => {
      setCropSourceLoadedKey(k => k + 1);
    }, []),
    mergeAspectRatios: MERGE_ASPECT_RATIOS,
    mergeAspectRatioId,
    setMergeAspectRatioId,
    mergeLayouts: MERGE_LAYOUTS,
    mergeLayoutId,
    setMergeLayoutId,
    handleNineGridSlice,
    gridSliceCount,
    setGridSliceCount,
    handleMergeImages,
  };
}
